import type { Organization } from '@/types/settings-api';

/**
 * Split an `Organization` draft into the two request bodies Flutter sends
 * (PROD-1766, PROD-1764).
 *
 * Flutter's save is TWO calls, not one — `settings_data_source_impl.dart:1204-1251`:
 *
 *   1. `PUT  /api/Organization/{orgId}`                  — org + licence fields
 *   2. `POST /api/Organization/UpdateContactDetails/{orgId}` — contact + billing
 *
 * React only ever made call 1, with the whole draft in the body, and
 * `UpdateContactDetails` appears nowhere in the codebase. Since Flutter
 * deliberately strips `contactDetails` / `billingDetails` from the PUT body, the
 * PUT almost certainly ignores them — which is why editing Contact Details
 * looked like it saved and did not.
 *
 * ## The PUT body IS stripped — and the strip is verified, not inferred
 *
 * PR #780 deliberately left Flutter's strip out, because it could not be sized
 * safely without knowing whether the PUT persisted those fields or ignored them.
 * That live write has since been run against the dev org, and both halves came
 * back unambiguous:
 *
 *   1. `PUT` with `contactDetails.faxNumber` changed  → **200, value unchanged**.
 *      The PUT genuinely ignores contact/billing; they were dead weight, and
 *      "editing Contact Details silently does nothing" was real data loss, not a
 *      wire-shape nit (PROD-1766).
 *   2. `PUT` with all ten keys stripped → **200, org intact**: `contactDetails`
 *      preserved, `name` / `accNo` / `organizationId` untouched.
 *
 * Result 2 is what unblocked this: it proves the .NET binder resolves
 * `organizationId` and `userId` from the route and headers rather than the body,
 * so removing them is safe. Without that check, stripping identity fields from a
 * write path would have been a guess dressed up as parity.
 *
 * See `PUT_STRIPPED_KEYS` below for the list and why each key is on it.
 */

/**
 * Keys Flutter removes from the PUT body (`settings_data_source_impl.dart:1207-1214`).
 *
 * Four kinds, and the reason matters if you are ever tempted to shorten this list:
 *
 *   - moved to the POST — `contactDetails`, `billingDetails`, `isBillingStatus`
 *   - server-owned / read-only — `lastSignIn`, `invitationStatus`, `acc_No`
 *     (+ `accNo`, the camelCase alias `useOrganization` flattens on read; React
 *     was sending BOTH casings)
 *   - identity carried by the URL, not the body — `organizationId`, `userId`
 *   - client-only staging — `imageBase64` (the logo upload is its own POST;
 *     `organization_model.dart:95-121` `toJson()` omits it entirely)
 *
 * `settings-api.ts:36-39` already documents `acc_No` as "never sent back on
 * update" — before this, the type and the wire disagreed.
 */
const PUT_STRIPPED_KEYS = [
  'lastSignIn',
  'invitationStatus',
  'contactDetails',
  'billingDetails',
  'isBillingStatus',
  'organizationId',
  'userId',
  'acc_No',
  'accNo',
  'imageBase64',
] as const;

/** Fields that must never reach the `PUT` body. */
type OrgWireBody = Omit<Organization, (typeof PUT_STRIPPED_KEYS)[number]>;

export interface OrgUpdateBodies {
  /** Body for `PUT /api/Organization/{orgId}`. */
  orgBody: OrgWireBody;
  /** Body for `POST /api/Organization/UpdateContactDetails/{orgId}`. */
  contactBody: Record<string, unknown>;
}

export function buildOrgUpdateBodies(draft: Organization): OrgUpdateBodies {
  // Strip is now verified, not inferred — see the docblock above. Both halves
  // were established with a live write against the dev org:
  //
  //   PUT with contactDetails.faxNumber changed  -> 200, value UNCHANGED
  //     (so the PUT genuinely ignores those fields; they were dead weight)
  //   PUT with all ten keys stripped             -> 200, org intact,
  //     contactDetails preserved, name/accNo/organizationId untouched
  //
  // That second result is the one that unblocked this: it proves the .NET binder
  // takes `organizationId` / `userId` from the route and headers, not the body.
  const orgBody = { ...draft } as Record<string, unknown>;
  for (const key of PUT_STRIPPED_KEYS) delete orgBody[key];

  // Mirrors Flutter's `conData` (`settings_data_source_impl.dart:1219-1232`),
  // which keeps exactly these keys and drops the org/licence ones.
  const contactBody: Record<string, unknown> = {
    organizationId: draft.organizationId,
    contactDetails: draft.contactDetails ?? null,
    billingDetails: draft.billingDetails ?? null,
    isBillingStatus: draft.isBillingStatus ?? null,
    photoURL: draft.photoURL ?? null,
    locations: draft.locations ?? null,
    frontLicenseIdURL: draft.frontLicenseIdURL ?? null,
    backLicenseIdURL: draft.backLicenseIdURL ?? null,
  };

  return { orgBody: orgBody as OrgWireBody, contactBody };
}
